import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import cx from "classnames";

// import { createChart } from "krasulya-lightweight-charts";
import TVjsApi from 'lib/chart/TVjsApi';
import { socketInstanceNew, subFun, unsubFun, unsubOrSub24Fun } from 'lib/chart/getSocket';

import { USD_DECIMALS, SWAP, INCREASE, CHART_PERIODS, getLiquidationPrice } from "lib/legacy";
// import { useChartPrices } from "domain/legacy";
import Tab from "../Tab/Tab";

import ChartTokenSelector from "./ChartTokenSelector";
import { useLocalStorageFun, useLocalStorageSerializeKey } from "lib/localStorage";
import { getTokenInfo } from "domain/tokens/utils";
import { usePrevious } from "lib/usePrevious";
import { expandDecimals, formatAmount, numberWithCommas } from "lib/numbers";
import { getToken, getTokens } from "config/tokens";
import { formatDateTime } from "lib/dates";
import {
  CACHE_EXCHANGE_CURRENT_KLINE_ABOUT_DATA_KEY,
} from "config/localStorage";
import { t, Trans } from "@lingui/macro";

const PRICE_LINE_TEXT_WIDTH = 15;

const timezoneOffset = -new Date().getTimezoneOffset() * 60;

export function getChartToken (swapOption, fromToken, toToken, chainId) {
  if (!fromToken || !toToken) {
    return;
  }

  if (swapOption !== SWAP) {
    return toToken;
  }

  if (fromToken.isUsdg && toToken.isUsdg) {
    return getTokens(chainId).find((t) => t.isStable);
  }
  if (fromToken.isUsdg) {
    return toToken;
  }
  if (toToken.isUsdg) {
    return fromToken;
  }

  if (fromToken.isStable && toToken.isStable) {
    return toToken;
  }
  if (fromToken.isStable) {
    return toToken;
  }
  if (toToken.isStable) {
    return fromToken;
  }

  return toToken;
}

const DEFAULT_PERIOD = "5m";

const getSeriesOptions = () => ({
  // https://github.com/tradingview/lightweight-charts/blob/master/docs/area-series.md
  lineColor: "#5472cc",
  topColor: "rgba(49, 69, 131, 0.4)",
  bottomColor: "rgba(42, 64, 103, 0.0)",
  lineWidth: 2,
  priceLineColor: "#3a3e5e",
  downColor: "#fa3c58",
  wickDownColor: "#fa3c58",
  upColor: "#0ecc83",
  wickUpColor: "#0ecc83",
  borderVisible: false,
  priceFormat: {
    type: "price",
    precision: 4,
    minMove: 0.0001,
  },
});

const getChartOptions = (width, height) => ({
  width,
  height,
  layout: {
    backgroundColor: "rgba(255, 255, 255, 0)",
    textColor: "#ccc",
    fontFamily: "Relative",
  },
  localization: {
    // https://github.com/tradingview/lightweight-charts/blob/master/docs/customization.md#time-format
    timeFormatter: (businessDayOrTimestamp) => {
      return formatDateTime(businessDayOrTimestamp - timezoneOffset);
    },
  },
  grid: {
    vertLines: {
      visible: true,
      color: "rgba(35, 38, 59, 1)",
      style: 2,
    },
    horzLines: {
      visible: true,
      color: "rgba(35, 38, 59, 1)",
      style: 2,
    },
  },
  // https://github.com/tradingview/lightweight-charts/blob/master/docs/time-scale.md#time-scale
  timeScale: {
    rightOffset: 5,
    borderVisible: false,
    barSpacing: 5,
    timeVisible: true,
    fixLeftEdge: true,
  },
  // https://github.com/tradingview/lightweight-charts/blob/master/docs/customization.md#price-axis
  priceScale: {
    borderVisible: false,
  },
  crosshair: {
    horzLine: {
      color: "#aaa",
    },
    vertLine: {
      color: "#aaa",
    },
    mode: 0,
  },
});

export default function ExchangeTVChart (props) {
  const {
    swapOption,
    fromTokenAddress,
    toTokenAddress,
    infoTokens,
    chainId,
    positions,
    savedShouldShowPositionLines,
    orders,
    setToTokenAddress,
  } = props;
  const [currentChart, setCurrentChart] = useState();
  const [currentSeries, setCurrentSeries] = useState();

  /**
   * Start a new variable content
   */
  const twentyFourData = useRef({
    amount: "",
    low: "",
    high: "",
    deltaPercentage: "",
    deltaPercentageStr: "",
  })
  const [currentKlineAboutData, setCurrentKlineAboutData] = useLocalStorageFun(
    CACHE_EXCHANGE_CURRENT_KLINE_ABOUT_DATA_KEY
  );
  const chartTokenOptionsRef = useRef();
  const chartQuoteRef = useRef(false);
  // End the new variable content



  let [period, setPeriod] = useLocalStorageSerializeKey([chainId, "Chart-period"], DEFAULT_PERIOD);
  if (!(period in CHART_PERIODS)) {
    period = DEFAULT_PERIOD;
  }

  const [hoveredCandlestick, setHoveredCandlestick] = useState();

  const fromToken = getTokenInfo(infoTokens, fromTokenAddress);
  const toToken = getTokenInfo(infoTokens, toTokenAddress);

  const [chartToken, setChartToken] = useState({
    maxPrice: null,
    minPrice: null,
  });
  useEffect(() => {
    const tmp = getChartToken(swapOption, fromToken, toToken, chainId);
    setChartToken(tmp);
  }, [swapOption, fromToken, toToken, chainId]);

  const symbol = chartToken ? (chartToken.isWrapped ? chartToken.baseSymbol : chartToken.symbol) : undefined;
  const marketName = chartToken ? symbol + "_USD" : undefined;
  const previousMarketName = usePrevious(marketName);

  const currentOrders = useMemo(() => {
    if (swapOption === SWAP || !chartToken) {
      return [];
    }

    return orders.filter((order) => {
      if (order.type === SWAP) {
        // we can't show non-stable to non-stable swap orders with existing charts
        // so to avoid users confusion we'll show only long/short orders
        return false;
      }

      const indexToken = getToken(chainId, order.indexToken);
      return order.indexToken === chartToken.address || (chartToken.isNative && indexToken.isWrapped);
    });
  }, [orders, chartToken, swapOption, chainId]);

  const ref = useRef(null);
  const chartRef = useRef(null);
  //  Start a new variable content
  const positionsLen = useRef(positions?.length)
  const ordersLen = useRef(currentOrders?.length)
  // End the new variable content

  const currentAveragePrice =
    chartToken.maxPrice && chartToken.minPrice ? chartToken.maxPrice.add(chartToken.minPrice).div(2) : null;
  // const [priceData, updatePriceData] = useChartPrices(
  //   chainId,
  //   chartToken.symbol,
  //   chartToken.isStable,
  //   period,
  //   currentAveragePrice
  // );

  // const [chartInited, setChartInited] = useState(false);
  // useEffect(() => {
  //   if (marketName !== previousMarketName) {
  //     setChartInited(false);
  //   }
  // }, [marketName, previousMarketName]);

  // const scaleChart = useCallback(() => {
  //   const from = Date.now() / 1000 - (7 * 24 * CHART_PERIODS[period]) / 2 + timezoneOffset;
  //   const to = Date.now() / 1000 + timezoneOffset;
  //   currentChart.timeScale().setVisibleRange({ from, to });
  // }, [currentChart, period]);

  // const onCrosshairMove = useCallback(
  //   (evt) => {
  //     if (!evt.time) {
  //       setHoveredCandlestick(null);
  //       return;
  //     }

  //     for (const point of evt.seriesPrices.values()) {
  //       setHoveredCandlestick((hoveredCandlestick) => {
  //         if (hoveredCandlestick && hoveredCandlestick.time === evt.time) {
  //           // rerender optimisations
  //           return hoveredCandlestick;
  //         }
  //         return {
  //           time: evt.time,
  //           ...point,
  //         };
  //       });
  //       break;
  //     }
  //   },
  //   [setHoveredCandlestick]
  // );

  // useEffect(() => {
  //   if (!ref.current || !priceData || !priceData.length || currentChart) {
  //     return;
  //   }

  //   const chart = createChart(
  //     chartRef.current,
  //     getChartOptions(chartRef.current.offsetWidth, chartRef.current.offsetHeight)
  //   );

  //   chart.subscribeCrosshairMove(onCrosshairMove);

  //   const series = chart.addCandlestickSeries(getSeriesOptions());

  //   setCurrentChart(chart);
  //   setCurrentSeries(series);
  // }, [ref, priceData, currentChart, onCrosshairMove]);

  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     updatePriceData(undefined, true);
  //   }, 60 * 1000);
  //   return () => clearInterval(interval);
  // }, [updatePriceData]);

  // useEffect(() => {
  //   if (!currentChart) {
  //     return;
  //   }
  //   const resizeChart = () => {
  //     currentChart.resize(chartRef.current.offsetWidth, chartRef.current.offsetHeight);
  //   };
  //   window.addEventListener("resize", resizeChart);
  //   return () => window.removeEventListener("resize", resizeChart);
  // }, [currentChart]);

  // useEffect(() => {
  //   if (currentSeries && priceData && priceData.length) {
  //     currentSeries.setData(priceData);

  //     if (!chartInited) {
  //       scaleChart();
  //       setChartInited(true);
  //     }
  //   }
  // }, [priceData, currentSeries, chartInited, scaleChart]);

  /**
   * Start adding content
   */
  if (!symbol) {
    localStorage.setItem("selected_symbol_name", "")
  }
  useEffect(() => {
    let readyState = socketInstanceNew?.socket?.readyState
    if (symbol && readyState == 1) {
      let oldSelectedSymbolName = localStorage.getItem("selected_symbol_name")
      if (oldSelectedSymbolName && oldSelectedSymbolName == symbol) {
        return
      }
      if (chartTokenOptionsRef && chartTokenOptionsRef.current && (!chartQuoteRef || !chartQuoteRef.current)) {
        subFun(chartTokenOptionsRef.current.getChartTokenOptions(), 'quote')
        chartQuoteRef.current = true
      }
      // Subscription: Cancel the previous subscription within the current 24 hours
      unsubOrSub24Fun(symbol)
      localStorage.setItem("selected_symbol_name", symbol)
    }
    // Uninstall the component to cancel the subscription
    return () => {
      // Cancel your subscription for 24 hours
      if (symbol) {
        unsubFun(symbol, "before24Price")
      }
      // Unsubscribe from quotes in all currencies
      // if (chartTokenOptionsRef && chartTokenOptionsRef.current) {
      //   let chart_token_options = localStorage.getItem("chart_token_options")
      //   if (chart_token_options) {
      //     chart_token_options = JSON.parse(chart_token_options)
      //     unsubFun(chart_token_options, "quote")
      //   }
      // }
    }
  }, [symbol, socketInstanceNew?.socket?.readyState])
  socketInstanceNew?.on('message', onMessageBind)
  function onMessageBind (evt) {
    if (!evt || evt.ping) {
      // console.log('[socket] Message333:onMessageBind', evt);
      return
    }
    let symbol_name = symbol.toLowerCase() + "usdt";
    if (evt.symbol != symbol_name) {
      // console.log("evt.symbol != symbol_name=", evt.symbol, symbol_name)
      return
    }
    if (evt.channel != "before24Price") {
      return
    }
    let tick = evt.tick
    let deltaPrice;
    let deltaPercentage;
    let deltaPercentageStr;
    if (tick?.rate) {
      deltaPercentage = tick.rate
      if (deltaPercentage > 0) {
        deltaPercentageStr = `+${(deltaPercentage * 100).toFixed(2)}%`;
      }
      if (deltaPercentage < 0) {
        deltaPercentageStr = `${(deltaPercentage * 100).toFixed(2)}%`;
      }
      if (deltaPercentage === 0) {
        deltaPercentageStr = "0.00";
      }
    }
    if (deltaPrice) {
      twentyFourData.current.amount = deltaPrice
    }
    if (tick?.low && tick?.low) {
      twentyFourData.current.low = tick.low
    }
    if (tick?.high && tick?.high) {
      twentyFourData.current.high = tick.high
    }
    if (deltaPercentage) {
      twentyFourData.current.deltaPercentage = deltaPercentage
      twentyFourData.current.deltaPercentageStr = deltaPercentageStr
    }
  }
  useEffect(() => {
    if (twentyFourData.current.amount && symbol) {
      let _obj = currentKlineAboutData
      if (currentKlineAboutData?.[symbol]) {
        _obj[symbol].amount = twentyFourData.current.amount
      } else {
        _obj[symbol] = twentyFourData.current
      }
      setCurrentKlineAboutData(_obj)
    }
  }, [twentyFourData.current.amount])
  useEffect(() => {
    if (twentyFourData.current?.high && symbol) {
      let _obj = currentKlineAboutData
      if (currentKlineAboutData?.[symbol]) {
        _obj[symbol].high = twentyFourData.current.high
      } else {
        _obj[symbol] = twentyFourData.current
      }
      setCurrentKlineAboutData(_obj)
    }
  }, [twentyFourData.current?.high])
  useEffect(() => {
    if (twentyFourData.current?.low && symbol) {
      let _obj = currentKlineAboutData
      if (currentKlineAboutData?.[symbol]) {
        _obj[symbol].low = twentyFourData.current.low
      } else {
        _obj[symbol] = twentyFourData.current
      }
      setCurrentKlineAboutData(_obj)
    }
  }, [twentyFourData.current?.low])
  useEffect(() => {
    if (twentyFourData.current?.deltaPercentage && symbol) {
      let _obj = currentKlineAboutData
      if (currentKlineAboutData?.[symbol]) {
        _obj[symbol].deltaPercentage = twentyFourData.current.deltaPercentage
        _obj[symbol].deltaPercentageStr = twentyFourData.current.deltaPercentageStr
      } else {
        _obj[symbol] = twentyFourData.current
      }
      setCurrentKlineAboutData(_obj)
    }
  }, [twentyFourData.current?.deltaPercentage])
  useEffect(() => {
    if (!ref.current || currentChart) {
      return;
    }
    if (chartToken.symbol) {
      const chart = TVjsApi(chartToken.symbol, period, chartRef.current.offsetWidth, chartRef.current.offsetHeight)
      // const series = chart
      setCurrentChart(chart);
      setCurrentSeries(1);
    }
  }, [ref, currentChart, chartToken]);
  useEffect(() => {
    if (currentChart) {
      currentChart.onChartReady(function () {
        let symbol_name_t = chartToken.symbol.toLowerCase() + "usdt"
        if (symbol_name_t == "wftmusdt") {
          symbol_name_t = "ftmusdt"
        }
        currentChart?.setSymbol?.(symbol_name_t, currentChart?.symbolInterval().interval)
        // set twentyFourData
        if (currentKlineAboutData?.[symbol]) {
          twentyFourData.current = currentKlineAboutData[symbol]
        }
      });
    }
  }, [symbol])
  // -----End of What's New----

  if (positionsLen?.current != positions.length) {
    positionsLen.current = positions.length
  }
  if (positionsLen?.current != currentOrders.length) {
    ordersLen.current = currentOrders.length
  }

  useEffect(() => {
    const lines = [];
    // console.log("positionsLen.current=", positionsLen.current)
    // console.log("ordersLen.current=", ordersLen.current)
    if (currentSeries && savedShouldShowPositionLines) {
      // if (currentChart && savedShouldShowPositionLines) {
      currentChart.onChartReady(function () {
        if (currentOrders && currentOrders.length > 0) {
          currentOrders.forEach((order) => {
            const indexToken = getToken(chainId, order.indexToken);
            let tokenSymbol;
            if (indexToken && indexToken.symbol) {
              tokenSymbol = indexToken.isWrapped ? indexToken.baseSymbol : indexToken.symbol;
            }
            const title = `${order.type === INCREASE ? "Inc." : "Dec."} ${tokenSymbol} ${order.isLong ? "Long" : "Short"
              }`;
            const color = "#3a3e5e";
            let tmp_order = currentChart?.activeChart().createOrderLine()
              ?.setPrice(parseFloat(formatAmount(order.triggerPrice, USD_DECIMALS, 2)))
              ?.setText(title.padEnd(PRICE_LINE_TEXT_WIDTH, " "))
              ?.setLineStyle(1)
              ?.setLineColor("#5394E2")
              ?.setBodyBorderColor("rgba(0,0,0,0)")
              ?.setBodyBackgroundColor("#5394E2")
              ?.setBodyTextColor("#ffffff")
              ?.setQuantityBorderColor("rgba(0,0,0,0)")
              ?.setQuantityBackgroundColor("#5394E2")
              ?.setQuantity("")
            lines.push(
              // currentSeries.createPriceLine({
              //   price: parseFloat(formatAmount(order.triggerPrice, USD_DECIMALS, 2)),
              //   color,
              //   title: title.padEnd(PRICE_LINE_TEXT_WIDTH, " "),
              // })
              tmp_order
            );
            // console.log("order-lines.length=", lines.length)
          });
        }
        if (positions && positions.length > 0) {
          const color = "#3a3e5e";
          positions.forEach((position) => {
            const tradeType = position.isLong ? t`Long` : t`Short`
            let tmp_a = currentChart?.activeChart().createPositionLine()
              ?.setPrice(parseFloat(formatAmount(position.averagePrice, USD_DECIMALS, 2)))
              ?.setText(t`Open ${position.indexToken.symbol} ${tradeType}`.padEnd(
                PRICE_LINE_TEXT_WIDTH,
                " "
              ))
              ?.setLineStyle(1)
              ?.setLineColor("#5394E2")
              ?.setBodyBorderColor("rgba(0,0,0,0)")
              ?.setBodyBackgroundColor("#5394E2")
              ?.setBodyTextColor("#ffffff")
              ?.setQuantityBorderColor("rgba(0,0,0,0)")
              ?.setQuantityBackgroundColor("#5394E2")
              ?.setQuantity("")
            lines.push(
              tmp_a
            );
            const liquidationPrice = getLiquidationPrice(position);
            let tmp_b = currentChart?.activeChart().createPositionLine()
              ?.setPrice(parseFloat(formatAmount(liquidationPrice, USD_DECIMALS, 2)))
              ?.setText(t`Liq. ${position.indexToken.symbol} ${tradeType}`.padEnd(
                PRICE_LINE_TEXT_WIDTH,
                " "
              ))
              ?.setLineStyle(1)
              ?.setLineColor("#5394E2")
              ?.setBodyBorderColor("rgba(0,0,0,0)")
              ?.setBodyBackgroundColor("#5394E2")
              ?.setBodyTextColor("#ffffff")
              ?.setQuantityBorderColor("rgba(0,0,0,0)")
              ?.setQuantityBackgroundColor("#5394E2")
            lines.push(
              tmp_b
            );
            // console.log("tmp_a=", tmp_b)
            // console.log("lines.length=", lines.length)
          });
        }
      })
    }
    return () => {
      // lines.forEach((line) => currentSeries.removePriceLine(line));
      lines.forEach((line) => {
        line?.remove()
      });
    };
  }, [currentOrders.length, positions.length, currentSeries, chainId, savedShouldShowPositionLines]);
  // }, [ordersLen.current, positionsLen.current, currentSeries, chainId, savedShouldShowPositionLines]);

  // const candleStatsHtml = useMemo(() => {
  //   if (!priceData) {
  //     return null;
  //   }
  //   const candlestick = hoveredCandlestick || priceData[priceData.length - 1];
  //   if (!candlestick) {
  //     return null;
  //   }

  //   const className = cx({
  //     "ExchangeChart-bottom-stats": true,
  //     positive: candlestick.open <= candlestick.close,
  //     negative: candlestick.open > candlestick.close,
  //     [`length-${String(parseInt(candlestick.close)).length}`]: true,
  //   });

  //   const toFixedNumbers = candlestick.close < 1 ? 4 : 2;

  //   return (
  //     <div className={className}>
  //       <span className="ExchangeChart-bottom-stats-label">O</span>
  //       <span className="ExchangeChart-bottom-stats-value">{candlestick.open.toFixed(toFixedNumbers)}</span>
  //       <span className="ExchangeChart-bottom-stats-label">H</span>
  //       <span className="ExchangeChart-bottom-stats-value">{candlestick.high.toFixed(toFixedNumbers)}</span>
  //       <span className="ExchangeChart-bottom-stats-label">L</span>
  //       <span className="ExchangeChart-bottom-stats-value">{candlestick.low.toFixed(toFixedNumbers)}</span>
  //       <span className="ExchangeChart-bottom-stats-label">C</span>
  //       <span className="ExchangeChart-bottom-stats-value">{candlestick.close.toFixed(toFixedNumbers)}</span>
  //     </div>
  //   );
  // }, [hoveredCandlestick, priceData]);

  // let high;
  // let low;
  // let deltaPrice;
  // let delta;
  // let deltaPercentage;
  // let deltaPercentageStr;

  // const now = parseInt(Date.now() / 1000);
  // const timeThreshold = now - 24 * 60 * 60;

  // if (priceData) {
  //   for (let i = priceData.length - 1; i > 0; i--) {
  //     const price = priceData[i];
  //     if (price.time < timeThreshold) {
  //       break;
  //     }
  //     if (!low) {
  //       low = price.low;
  //     }
  //     if (!high) {
  //       high = price.high;
  //     }

  //     if (price.high > high) {
  //       high = price.high;
  //     }
  //     if (price.low < low) {
  //       low = price.low;
  //     }

  //     deltaPrice = price.open;
  //   }
  // }

  // if (deltaPrice && currentAveragePrice) {
  //   const average = parseFloat(formatAmount(currentAveragePrice, USD_DECIMALS, 2));
  //   delta = average - deltaPrice;
  //   deltaPercentage = (delta * 100) / average;
  //   if (deltaPercentage > 0) {
  //     deltaPercentageStr = `+${deltaPercentage.toFixed(2)}%`;
  //   } else {
  //     deltaPercentageStr = `${deltaPercentage.toFixed(2)}%`;
  //   }
  //   if (deltaPercentage === 0) {
  //     deltaPercentageStr = "0.00";
  //   }
  // }

  if (!chartToken) {
    return null;
  }

  const onSelectToken = (token) => {
    const tmp = getTokenInfo(infoTokens, token.address);
    setChartToken(tmp);
    setToTokenAddress(swapOption, token.address);
  };
  return (
    <div className="ExchangeChart tv" ref={ref}>
      <div className="ExchangeChart-top App-box App-box-border">
        <div className="ExchangeChart-top-inner">
          <div>
            <div className="ExchangeChart-title">
              <ChartTokenSelector
                ref={chartTokenOptionsRef}
                chainId={chainId}
                selectedToken={chartToken}
                swapOption={swapOption}
                infoTokens={infoTokens}
                onSelectToken={onSelectToken}
                className="chart-token-selector"
              />
            </div>
          </div>
          <div className="" style={{ display: "none" }}>
            {currentKlineAboutData && `${currentKlineAboutData?.[symbol]?.amount}换行;`}
            {twentyFourData.current && `${twentyFourData.current.amount}换行;`}
          </div>
          <div>
            <div className="ExchangeChart-main-price">
              {chartToken.maxPrice && formatAmount(chartToken.maxPrice, USD_DECIMALS, 2, true)}
            </div>
            <div className="ExchangeChart-info-label">
              ${chartToken.minPrice && formatAmount(chartToken.minPrice, USD_DECIMALS, 2, true)}
            </div>
          </div>
          <div>
            <div className="ExchangeChart-info-label"><Trans>24h Change</Trans></div>
            {/* <div className={cx({ positive: deltaPercentage > 0, negative: deltaPercentage < 0 })}>
              {!deltaPercentageStr && "-"}
              {deltaPercentageStr && deltaPercentageStr}
            </div> */}
            <div className={cx({ positive: (twentyFourData.current && twentyFourData.current.deltaPercentage > 0), negative: (twentyFourData.current && twentyFourData.current.deltaPercentage < 0) })}>
              {!twentyFourData.current && !currentKlineAboutData && "-"}
              {!twentyFourData.current && currentKlineAboutData && !currentKlineAboutData?.[symbol]?.deltaPercentageStr && "-"}
              {twentyFourData.current && !twentyFourData.current.deltaPercentageStr && currentKlineAboutData && currentKlineAboutData?.[symbol]?.deltaPercentageStr}
              {twentyFourData.current && twentyFourData.current.deltaPercentageStr && twentyFourData.current.deltaPercentageStr}
            </div>
          </div>
          <div className="ExchangeChart-additional-info">
            <div className="ExchangeChart-info-label"><Trans>24h High</Trans></div>
            <div>
              {/* {!high && "-"}
              {high && numberWithCommas(high.toFixed(high < 1 ? 4 : 2))} */}
              {!twentyFourData.current && !currentKlineAboutData && "-"}
              {!twentyFourData.current && currentKlineAboutData && !currentKlineAboutData?.[symbol]?.high && "-"}
              {twentyFourData.current && !twentyFourData.current.high && currentKlineAboutData && currentKlineAboutData?.[symbol]?.high && numberWithCommas(parseFloat(currentKlineAboutData[symbol].high).toFixed(currentKlineAboutData[symbol].high < 1 ? 4 : 2))}
              {twentyFourData.current && twentyFourData.current.high && numberWithCommas(parseFloat(twentyFourData.current.high).toFixed(twentyFourData.current.high < 1 ? 4 : 2))}
            </div>
          </div>
          <div className="ExchangeChart-additional-info">
            <div className="ExchangeChart-info-label"><Trans>24h Low</Trans></div>
            <div>
              {/* {!low && "-"}
              {low && numberWithCommas(low.toFixed(low < 1 ? 4 : 2))} */}
              {!twentyFourData.current && !currentKlineAboutData && "-"}
              {!twentyFourData.current && currentKlineAboutData && !currentKlineAboutData?.[symbol]?.low && "-"}
              {twentyFourData.current && !twentyFourData.current.low && currentKlineAboutData && currentKlineAboutData?.[symbol]?.low && numberWithCommas(parseFloat(currentKlineAboutData[symbol].low).toFixed(currentKlineAboutData[symbol].low < 1 ? 4 : 2))}
              {twentyFourData.current && twentyFourData.current.low && numberWithCommas(parseFloat(twentyFourData.current.low).toFixed(twentyFourData.current.low < 1 ? 4 : 2))}
            </div>
          </div>
        </div>
      </div>
      <div className="ExchangeChart-bottom App-box App-box-border">
        {/* <div className="ExchangeChart-bottom-header">
          <div className="ExchangeChart-bottom-controls">
            <Tab options={Object.keys(CHART_PERIODS)} option={period} setOption={setPeriod} />
          </div>
          {candleStatsHtml}
        </div> */}
        <div id="tv_chart_container" className="ExchangeChart-bottom-content" ref={chartRef}></div>
      </div>
    </div>
  );
}
